import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { LanguageProvider } from './i18n';
import Layout from './components/Layout';
import Login from './pages/Login';
import PublicBooking from './pages/PublicBooking';
const Dashboard = lazyPage(() => import('./pages/Dashboard'));
const Assets = lazyPage(() => import('./pages/Assets'));
const WorkOrders = lazyPage(() => import('./pages/WorkOrders'));
const Spaces = lazyPage(() => import('./pages/Spaces'));
const Maintenance = lazyPage(() => import('./pages/Maintenance'));
const Users = lazyPage(() => import('./pages/Users'));
const Settings = lazyPage(() => import('./pages/Settings'));
const MyTasks = lazyPage(() => import('./pages/MyTasks'));
const Reports = lazyPage(() => import('./pages/Reports'));
const AboutPage = lazyPage(() => import('./pages/AboutPage'));
const ServicesPage = lazyPage(() => import('./pages/ServicesPage'));
const LegalPage = lazyPage(() => import('./pages/LegalPage'));
const BookingPage = lazyPage(() => import('./pages/BookingPage'));
const ReportPage = lazyPage(() => import('./pages/ReportPage'));
const PayPage = lazyPage(() => import('./pages/PayPage'));
const Payments = lazyPage(() => import('./pages/Payments'));
const ContactPage = lazyPage(() => import('./pages/ContactPage'));
const Bookings = lazyPage(() => import('./pages/Bookings'));
const Inventory = lazyPage(() => import('./pages/Inventory'));
const Messages = lazyPage(() => import('./pages/Messages'));
const VendorQuote = lazyPage(() => import('./pages/VendorQuote'));
const ForgotPassword = lazyPage(() => import('./pages/ForgotPassword'));
const ResetPassword = lazyPage(() => import('./pages/ResetPassword'));
const Developer = lazyPage(() => import('./pages/Developer'));
const SignUp = lazyPage(() => import('./pages/SignUp'));
const VerifyEmail = lazyPage(() => import('./pages/VerifyEmail'));
const Billing = lazyPage(() => import('./pages/Billing'));
const BillingResult = lazyPage(() => import('./pages/BillingResult'));
const Requests = lazyPage(() => import('./pages/Requests'));

// Pages are downloaded only when someone opens them, so the first screen loads faster on slow
// connections. After a new release the old file names disappear; reload once to pick up the new ones.
function lazyPage(loader) {
  return React.lazy(() => loader().then(module => {
    try { sessionStorage.removeItem('fms_chunk_reload'); } catch { /* ignore */ }
    return module;
  }).catch(error => {
    let already = false;
    try { already = sessionStorage.getItem('fms_chunk_reload') === '1'; } catch { /* ignore */ }
    if (!already) {
      try { sessionStorage.setItem('fms_chunk_reload', '1'); } catch { /* ignore */ }
      window.location.reload();
      return new Promise(() => {});
    }
    throw error;
  }));
}

function PageLoading() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }} role="status" aria-label="Loading">
      <div className="spinner-border text-primary" />
    </div>
  );
}

function getUser() {
  return JSON.parse(localStorage.getItem('fms_user') || '{}');
}

// Send a signed-out visitor to the login page and bring them back to the page they asked for
// (for example a link in a notification email) once they have signed in.
function LoginRedirect() {
  const next = window.location.pathname + window.location.search;
  return <Navigate to={next.startsWith('/app/') ? `/login?next=${encodeURIComponent(next)}` : '/login'} replace />;
}

function PrivateRoute({ children }) {
  return localStorage.getItem('fms_token') ? children : <LoginRedirect />;
}

function AdminRoute({ children }) {
  if (!localStorage.getItem('fms_token')) return <LoginRedirect />;
  return ['admin', 'developer'].includes(getUser().role) ? children : <Navigate to="/app/dashboard" replace />;
}

function ManagerRoute({ children }) {
  if (!localStorage.getItem('fms_token')) return <LoginRedirect />;
  const role = getUser().role;
  return (role === 'admin' || role === 'manager') ? children : <Navigate to="/app/my-tasks" replace />;
}

function DeveloperRoute({ children }) {
  if (!localStorage.getItem('fms_token')) return <LoginRedirect />;
  return getUser().role === 'developer' ? children : <Navigate to="/app/dashboard" replace />;
}

// Short public link for an organization: /o/acme-co opens its public booking page,
// /o/acme-co/report its report page, etc. (same as /?system=acme-co, /report?system=acme-co).
const ORG_PAGES = ['about', 'services', 'booking', 'report', 'contact', 'login', 'signup'];
function OrgRedirect() {
  const { slug, page } = useParams();
  const path = page && ORG_PAGES.includes(page) ? `/${page}` : '/';
  window.location.replace(`${path}?system=${encodeURIComponent(slug || '')}`);
  return null;
}

function AppDefaultRoute() {
  const token = localStorage.getItem('fms_token');
  if (!token) return <PublicBooking />;
  const role = getUser().role;
  return role === 'employee'
    ? <Navigate to="/app/my-tasks" replace />
    : role === 'developer' ? <Navigate to="/app/developer" replace />
    : <Navigate to="/app/dashboard" replace />;
}

export default function App() {
  // The login lives in the browser's shared storage. When another tab signs in
  // (or out), the session this tab was showing is over, so send it to the login
  // page instead of letting it keep showing the previous user.
  useEffect(() => {
    const onStorage = event => {
      if (event.key !== 'fms_token' || event.newValue === event.oldValue) return;
      if (window.location.pathname.startsWith('/app')) window.location.href = '/login';
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <LanguageProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoading />}>
        <Routes>
          {/* Public — no login required */}
          <Route path="/" element={<AppDefaultRoute />} />
          <Route path="/o/:slug" element={<OrgRedirect />} />
          <Route path="/o/:slug/:page" element={<OrgRedirect />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/privacy-policy" element={<LegalPage type="privacy" />} />
          <Route path="/terms-of-service" element={<LegalPage type="terms" />} />
          <Route path="/booking" element={<BookingPage />} />
          <Route path="/report" element={<ReportPage />} />
          <Route path="/pay/:token" element={<PayPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/billing/result" element={<BillingResult />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/vendor/asset-quote/:token" element={<VendorQuote />} />

          {/* Authenticated app */}
          <Route path="/app" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<ManagerRoute><Dashboard /></ManagerRoute>} />
            <Route path="assets" element={<ManagerRoute><Assets /></ManagerRoute>} />
            <Route path="work-orders" element={<ManagerRoute><WorkOrders /></ManagerRoute>} />
            <Route path="spaces" element={<ManagerRoute><Spaces /></ManagerRoute>} />
            <Route path="maintenance" element={<ManagerRoute><Maintenance /></ManagerRoute>} />
            <Route path="bookings" element={<ManagerRoute><Bookings /></ManagerRoute>} />
            <Route path="payments" element={<ManagerRoute><Payments /></ManagerRoute>} />
            <Route path="inventory" element={<ManagerRoute><Inventory /></ManagerRoute>} />
            <Route path="requests" element={<PrivateRoute><Requests /></PrivateRoute>} />
            <Route path="messages" element={<Messages />} />
            <Route path="reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
            <Route path="my-tasks" element={<PrivateRoute><MyTasks /></PrivateRoute>} />
            <Route path="users" element={<PrivateRoute><Users /></PrivateRoute>} />
            <Route path="settings" element={<AdminRoute><Settings /></AdminRoute>} />
            <Route path="billing" element={<AdminRoute><Billing /></AdminRoute>} />
            <Route path="developer" element={<DeveloperRoute><Developer /></DeveloperRoute>} />
          </Route>

          {/* Legacy short paths */}
          <Route path="/dashboard" element={<PrivateRoute><Navigate to="/app/dashboard" replace /></PrivateRoute>} />
          <Route path="/my-tasks" element={<PrivateRoute><Navigate to="/app/my-tasks" replace /></PrivateRoute>} />
          <Route path="/assets" element={<PrivateRoute><Navigate to="/app/assets" replace /></PrivateRoute>} />
          <Route path="/work-orders" element={<PrivateRoute><Navigate to="/app/work-orders" replace /></PrivateRoute>} />
          <Route path="/spaces" element={<PrivateRoute><Navigate to="/app/spaces" replace /></PrivateRoute>} />
          <Route path="/maintenance" element={<PrivateRoute><Navigate to="/app/maintenance" replace /></PrivateRoute>} />
          <Route path="/bookings" element={<PrivateRoute><Navigate to="/app/bookings" replace /></PrivateRoute>} />
          <Route path="/inventory" element={<PrivateRoute><Navigate to="/app/inventory" replace /></PrivateRoute>} />
          <Route path="/requests" element={<PrivateRoute><Navigate to={`/app/requests${window.location.search}`} replace /></PrivateRoute>} />
          <Route path="/messages" element={<PrivateRoute><Navigate to="/app/messages" replace /></PrivateRoute>} />
          <Route path="/reports" element={<PrivateRoute><Navigate to="/app/reports" replace /></PrivateRoute>} />
          <Route path="/users" element={<PrivateRoute><Navigate to="/app/users" replace /></PrivateRoute>} />
          <Route path="/settings" element={<PrivateRoute><Navigate to="/app/settings" replace /></PrivateRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </LanguageProvider>
  );
}
